import { useCallback, useEffect, useMemo } from 'react';
import { LaunchBulkUpdateInternalNotes } from 'src/components/common/BulkActions/BulkUpdateInternalNotesAction';
import { useBulkUpdateInternalNotesPermissions } from 'src/components/common/BulkActions/BulkUpdateInternalNotesAction/useBulkUpdateInternalNotesPermissions';
import {
  IPosEntity,
  useActivePosEntityContext,
} from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useCommandMenuContext } from 'src/contexts/CommandMenuContext/CommandMenuContext';
import { DialogId } from 'src/contexts/DialogContext/DialogContext';
import { useDialog } from 'src/contexts/DialogContext/useDialog';
import { BulkUpdateInternalNotesDialog } from 'src/dialogs/BulkEdits/BulkUpdateInternalNotesDialog';
import { useEventCallback } from 'src/hooks/useEventCallback';
import { ContentId } from 'src/utils/constants/contentId';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  ActionOutboxEntityType,
  BulkEditListingClient,
  BulkEditPreviewWithDetails,
  CommandOptionType,
  Event,
  ListingQuery,
} from 'src/WebApiController';

import { INVENTORY_BULK_UPDATE_INTERNAL_NOTES_UPDATE_KEY } from '../../InventoryActionDropdown.constants';
import { BulkActionItem } from '../types';

export const useBulkUpdateInternalNotesAction = (
  filterQueryWithEventIds: ListingQuery,
  setIsLoading: (v: boolean) => void,
  isLoading?: boolean,
  event?: Event
) => {
  const { registerCommand, unregisterCommand } = useCommandMenuContext();
  const { hasEditInternalNotesPermissions } =
    useBulkUpdateInternalNotesPermissions({
      entityType: ActionOutboxEntityType.Listing,
    });

  const { activeAccountWebClientConfig } = useAppContext();
  const { setActivePosEntity } = useActivePosEntityContext<IPosEntity>();
  const updateKey = event
    ? `listing-internal-notes-event-${event.virtId}`
    : INVENTORY_BULK_UPDATE_INTERNAL_NOTES_UPDATE_KEY;
  const {
    eventsExpansion: { setListItemExpansion, refreshExpandedListItems },
  } = useCatalogDataContext();
  const { showErrorDialog } = useClientLogContext();

  const {
    openDialog: openBulkUpdateInternalNotesDialog,
    closeDialog: closeBulkUpdateInternalNotesDialog,
  } = useDialog(
    DialogId.BulkUpdateInternalNotes,
    BulkUpdateInternalNotesDialog
  );

  const onSubmit = useCallback(
    async (
      isUpdate: boolean | null,
      searchString: string | null,
      replaceString: string | null,
      appendString: string | null,
      supportBackgroundProcess?: boolean,
      onPreviewReceived?: (preview: BulkEditPreviewWithDetails) => void,
      preview?: BulkEditPreviewWithDetails
    ) => {
      setIsLoading(true);
      if (onPreviewReceived) {
        await tryInvokeApi(
          async () => {
            const preview = await new BulkEditListingClient(
              activeAccountWebClientConfig
            ).bulkUpdateInternalNotesPreview(filterQueryWithEventIds);

            onPreviewReceived(preview);
          },
          (error) => {
            showErrorDialog('bulkUpdateInternalNotesPreview', error, {
              trackErrorData: { filterQueryWithEventIds },
            });
          },
          () => {
            if (!supportBackgroundProcess) {
              setIsLoading(false);
            }
          }
        );
      } else if (
        isUpdate != null &&
        searchString != null &&
        replaceString != null &&
        appendString != null
      ) {
        await tryInvokeApi(
          async () => {
            const listingClient = new BulkEditListingClient(
              activeAccountWebClientConfig
            );

            const succeeded = await listingClient.bulkUpdateInternalNotes(
              preview!.preview,
              updateKey,
              isUpdate,
              searchString,
              replaceString,
              appendString,
              supportBackgroundProcess
            );

            if (!supportBackgroundProcess) {
              if (succeeded) {
                // Invalidate the activePosEntity, so that when clicking on listingDetails
                // we force re-loading the listing and newest pricing settings can be displayed
                setActivePosEntity(0);
                await refreshExpandedListItems();
              }

              closeBulkUpdateInternalNotesDialog();
            }
          },
          (error) => {
            showErrorDialog('bulkUpdateInternalNotes', error, {
              trackErrorData: {
                isUpdate,
                searchString,
                replaceString,
                preview,
              },
            });
          },
          () => {
            if (!supportBackgroundProcess) {
              setIsLoading(false);
            }
          }
        );
      }
    },
    [
      setIsLoading,
      activeAccountWebClientConfig,
      filterQueryWithEventIds,
      showErrorDialog,
      updateKey,
      closeBulkUpdateInternalNotesDialog,
      setActivePosEntity,
      refreshExpandedListItems,
    ]
  );

  const onChangeClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (event) {
        setListItemExpansion(true, [event.virtId]);
      }

      openBulkUpdateInternalNotesDialog({
        updateKey,
        onOkay: onSubmit,
        entityType: ActionOutboxEntityType.Listing,
        onClosed: () => {
          setIsLoading(false);
        },
        onCancel: closeBulkUpdateInternalNotesDialog,
      });
    },
    [
      closeBulkUpdateInternalNotesDialog,
      event,
      onSubmit,
      openBulkUpdateInternalNotesDialog,
      setIsLoading,
      setListItemExpansion,
      updateKey,
    ]
  );

  const openDialogStableRef = useEventCallback(() => {
    openBulkUpdateInternalNotesDialog({
      updateKey,
      onOkay: onSubmit,
      entityType: ActionOutboxEntityType.Listing,
      onClosed: () => {
        setIsLoading(false);
      },
      onCancel: closeBulkUpdateInternalNotesDialog,
    });
  });

  useEffect(() => {
    registerCommand({
      command: {
        id: CommandOptionType.ListingBulkEditInternalNotes,
        onSelect: openDialogStableRef,
      },
      disabled: !hasEditInternalNotesPermissions,
    });

    return () => {
      unregisterCommand({
        commandId: CommandOptionType.ListingBulkEditInternalNotes,
        disabled: !hasEditInternalNotesPermissions,
      });
    };
  }, [
    hasEditInternalNotesPermissions,
    openDialogStableRef,
    registerCommand,
    unregisterCommand,
  ]);

  const actionItems = useMemo<BulkActionItem[]>(() => {
    if (!hasEditInternalNotesPermissions) {
      return [];
    }

    return [
      {
        disabled: isLoading,
        onClick: onChangeClick,
        title: ContentId.UpdatePrivateNotes,
      },
    ];
  }, [hasEditInternalNotesPermissions, isLoading, onChangeClick]);

  return {
    dropDown: (
      <LaunchBulkUpdateInternalNotes
        key="UpdateInternalNotes"
        onClick={onChangeClick}
        disabled={isLoading}
        entityType={ActionOutboxEntityType.Listing}
      />
    ),
    actionItems,
  };
};
